import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { RecipeListItem } from "../lib/types";
import { Ornament } from "./Ornament";

interface RecipePickerProps {
  /** Short caption like "Monday · 13 Apr" so the picker can say which day. */
  dayLabel: string;
  onPick: (recipe: RecipeListItem) => void;
  onClose: () => void;
}

/**
 * Modal recipe picker. Fetches /api/recipes (cached by react-query under
 * the same key RecipesIndexPage uses) and lets the user click a title.
 */
export function RecipePicker({ dayLabel, onPick, onClose }: RecipePickerProps) {
  const [query, setQuery] = useState("");

  const { data, isLoading } = useQuery<RecipeListItem[]>({
    queryKey: ["recipes"],
    queryFn: () => api.get<RecipeListItem[]>("/api/recipes"),
  });

  // Close on Escape for accessibility.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter((r) => r.title.toLowerCase().includes(q));
  }, [data, query]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={`Pick a recipe for ${dayLabel}`}
    >
      {/* Parchment-tinted backdrop so the book is still faintly visible. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-ink/35 backdrop-blur-[2px]"
      />

      <div
        className="relative my-12 w-full max-w-2xl border border-ink/70 bg-parchment px-10 py-12 shadow-[0_40px_80px_-30px_rgba(28,20,16,0.6)] animate-fade-up md:px-14 md:py-14"
        style={{ animationDelay: "30ms" }}
      >
        <div className="mb-8 flex items-start justify-between gap-6">
          <div>
            <div className="label mb-2 text-sienna">Choose a recipe</div>
            <div className="font-display text-4xl font-light leading-none text-ink">
              For <span className="display-italic">{dayLabel}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="label text-ink-muted transition-colors hover:text-sienna"
            aria-label="Close picker"
          >
            Close ×
          </button>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <Ornament className="h-3 w-14 text-sienna" />
          <span className="hairline-ink flex-1" />
        </div>

        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the library…"
          className="field-input mb-6"
        />

        <div className="max-h-[50vh] overflow-y-auto pr-2">
          {isLoading && (
            <p className="label text-ink-muted">Gathering the library…</p>
          )}

          {!isLoading && (data?.length ?? 0) === 0 && (
            <p className="font-body italic text-ink-walnut">
              The library is empty — write a recipe first.
            </p>
          )}

          {!isLoading && (data?.length ?? 0) > 0 && filtered.length === 0 && (
            <p className="font-body italic text-ink-walnut">
              Nothing in the library matches “{query}”.
            </p>
          )}

          <ul className="divide-y divide-ink/15">
            {filtered.map((r, i) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onPick(r)}
                  className="group grid w-full grid-cols-[auto_1fr_auto] items-baseline gap-x-5 py-4 text-left transition-colors duration-300 hover:bg-parchment-deep/60"
                >
                  <span className="label text-ink-muted">
                    N° {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-2xl font-light leading-tight text-ink transition-colors duration-300 group-hover:text-sienna">
                    {r.title}
                  </span>
                  <span className="label text-ink-walnut">
                    serves {r.servings}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
